import { App, AppConfig, AppLoaderConstructor } from '@nab/x-types';
import { XV1AppLoader } from './XV1AppLoader';
import { XV2AppLoader } from './XV2AppLoader';

export type LoaderMatcher = string | string[] | LoaderMatchFn;

export interface LoaderMatchFn {
    (appMetaData: App, appConfig?: AppConfig): boolean;
}

export interface LoaderStrategy<APP extends App = App> {
    constructor: AppLoaderConstructor<APP>;
    supports: LoaderMatcher;
}

const customLoaders: LoaderStrategy[] = [];
const defaultLoaders: LoaderStrategy[] = [
    {
        constructor: XV2AppLoader,
        supports: (appMetaData: App) => appMetaData.appType === 'XV2' || appMetaData.appType === 'XV2SPA'
    },
    { constructor: XV1AppLoader, supports: (appMetaData: App) => appMetaData.appType === 'XV1' }
];

export function registerLoader(strategy: LoaderStrategy, isLowPriority: boolean = false) {
    let matcher: LoaderMatchFn;

    if (typeof strategy.supports === 'string' || Array.isArray(strategy.supports)) {
        const appType = typeof strategy.supports === 'string' ? [strategy.supports] : strategy.supports;
        matcher = (appMetaData: App, appConfig: AppConfig) => appType.indexOf(appMetaData.appType) !== -1;
    } else {
        matcher = strategy.supports;
    }

    const augmentedStrategy = {
        constructor: strategy.constructor,
        supports: matcher
    };

    if (isLowPriority) {
        customLoaders.push(augmentedStrategy);
    } else {
        customLoaders.unshift(augmentedStrategy);
    }
}

export function getLoaderConstructor<APP extends App = App>(appMetaData: App, appConfig?: AppConfig): AppLoaderConstructor<APP> {
    const loaderStrategy = <LoaderStrategy<APP>>(
        [...customLoaders, ...defaultLoaders].find((l) => (<LoaderMatchFn>l.supports)(appMetaData, appConfig))
    );
    if (!loaderStrategy) {
        throw new Error(`Failed to find a suitable app loader for app ${appMetaData.id} with appType ${appMetaData.appType}`);
    }
    return loaderStrategy.constructor;
}

export function reset() {
    customLoaders.splice(0, customLoaders.length);
}
